import axios from 'axios';

// const API_URL = 'http://localhost:8000/api_v1/';
const API_URL = 'https://e-landgraf.ru/api_v1/';

// Функция для получения CSRF токена из cookies
function getCookie(name) {
    let cookieValue = null;
    if (document.cookie && document.cookie !== '') {
        const cookies = document.cookie.split(';');
        for (let i = 0; i < cookies.length; i++) {
            const cookie = cookies[i].trim();
            if (cookie.substring(0, name.length + 1) === (name + '=')) {
                cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
                break;
            }
        }
    }
    return cookieValue;
}

// Функция для получения CSRF токена
function getCsrfToken() {
    return getCookie('csrftoken');
}

const api = axios.create({
    baseURL: API_URL,
    withCredentials: true,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Interceptor для автоматической отправки CSRF токена
api.interceptors.request.use(
    (config) => {
        // Для методов, которые требуют CSRF (POST, PUT, PATCH, DELETE)
        // Исключаем login и logout, так как для них CSRF отключен
        if (['post', 'put', 'patch', 'delete'].includes(config.method.toLowerCase()) &&
            !config.url?.endsWith('/login/')) {
            const csrfToken = getCsrfToken();
            if (csrfToken) {
                config.headers['X-CSRFToken'] = csrfToken;
            }
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Функция для получения CSRF токена с сервера (вызывается при инициализации)
export const getCsrfTokenFromServer = async () => {
    try {
        const response = await axios.get(`${API_URL}adminarea/users/get-csrf-token/`, {
            withCredentials: true
        });
        if (response.data?.csrf_token) {
            // Устанавливаем токен в cookie
            document.cookie = `csrftoken=${response.data.csrf_token}; path=/`;
            return response.data.csrf_token;
        }
    } catch (error) {
        console.warn('Не удалось получить CSRF токен:', error);
    }
    return null;
};

export const authAPI = {
    login: async (username, password) => {
        try {
            const response = await api.post('adminarea/users/login/', {username, password});
            return {success: true, data: response.data};
        } catch (error) {
            const data = error.response?.data;
            return {
                success: false,
                error: data?.error || 'Неверные учетные данные',
                access_expired: Boolean(data?.access_expired),
            };
        }
    },

    logout: async () => {
        try {
            await api.post('adminarea/users/logout/');
            return {success: true};
        } catch (error) {
            return {success: false};
        }
    },

    checkAuth: async () => {
        try {
            const response = await api.get('adminarea/users/check/');
            return {success: true, data: response.data};
        } catch (error) {
            return {success: false};
        }
    }
};

export const userAPI = {
    create: async (userData) => {
        try {
            const response = await api.post('adminarea/users/', userData);
            return {success: true, data: response.data};
        } catch (error) {
            const errorData = error.response?.data;
            return {
                success: false,
                error: errorData?.errors || errorData || 'Ошибка при создании пользователя'
            };
        }
    },

    update: async (userId, userData) => {
        try {
            const response = await api.patch(`adminarea/users/${userId}/`, userData);
            return {success: true, data: response.data};
        } catch (error) {
            const errorData = error.response?.data;
            return {
                success: false,
                error: errorData?.errors || errorData || 'Ошибка при обновлении пользователя'
            };
        }
    },

    delete: async (userId) => {
        try {
            const response = await api.delete(`adminarea/users/${userId}/`);
            return {success: true, data: response.data};
        } catch (error) {
            const errorData = error.response?.data;
            return {
                success: false,
                error: errorData?.errors || errorData || 'Ошибка при удалении пользователя'
            };
        }
    },

    getCurrent: async () => {
        try {
            const response = await api.get('adminarea/users/check/');
            return {success: true, data: response.data};
        } catch (error) {
            return {success: false};
        }
    },

    getAll: async () => {
        try {
            const response = await api.get('adminarea/users/');
            return {success: true, data: response.data};
        } catch (error) {
            return {
                success: false,
                error: error.response?.data || 'Ошибка при получении списка пользователей'
            };
        }
    }
};

const unwrapData = (response) => response.data?.data ?? response.data;

export const playlistAPI = {
    getAll: async () => {
        try {
            const response = await api.get('audioposobie/playlists/');
            const body = response.data;
            if (body?.success === false) {
                return { success: false, error: body.errors || body.error };
            }
            return { success: true, data: unwrapData(response) };
        } catch (error) {
            const status = error.response?.status;
            return {
                success: false,
                error:
                    status === 404
                        ? 'API плейлистов недоступен. Перезапустите Django-сервер.'
                        : error.response?.data || 'Ошибка при получении плейлистов',
                status,
            };
        }
    },

    getById: async (playlistId) => {
        try {
            const response = await api.get(`audioposobie/playlists/${playlistId}/`);
            return { success: true, data: unwrapData(response) };
        } catch (error) {
            return {
                success: false,
                error: error.response?.data || 'Плейлист не найден',
            };
        }
    },

    create: async (name) => {
        try {
            const response = await api.post('audioposobie/playlists/', { name });
            const body = response.data;
            if (body?.success === false) {
                return {
                    success: false,
                    error: body.errors || body.error || 'Ошибка при создании плейлиста',
                };
            }
            return { success: true, data: unwrapData(response) };
        } catch (error) {
            const errorData = error.response?.data;
            const status = error.response?.status;
            return {
                success: false,
                error:
                    status === 404
                        ? 'API плейлистов недоступен. Перезапустите Django-сервер.'
                        : errorData?.errors || errorData?.error || 'Ошибка при создании плейлиста',
                status,
            };
        }
    },

    update: async (playlistId, name) => {
        try {
            const response = await api.patch(`audioposobie/playlists/${playlistId}/`, { name });
            return { success: true, data: unwrapData(response) };
        } catch (error) {
            const errorData = error.response?.data;
            return {
                success: false,
                error: errorData?.errors || errorData?.error || 'Ошибка при обновлении плейлиста',
            };
        }
    },

    delete: async (playlistId) => {
        try {
            const response = await api.delete(`audioposobie/playlists/${playlistId}/`);
            return { success: true, data: response.data };
        } catch (error) {
            const errorData = error.response?.data;
            return {
                success: false,
                error: errorData?.error || 'Ошибка при удалении плейлиста',
            };
        }
    },

    addTrack: async (playlistId, trackData) => {
        try {
            const response = await api.post(
                `audioposobie/playlists/${playlistId}/tracks/`,
                trackData
            );
            return { success: true, data: unwrapData(response) };
        } catch (error) {
            const errorData = error.response?.data;
            return {
                success: false,
                error: errorData?.error || errorData?.errors || 'Ошибка при добавлении трека',
                status: error.response?.status,
            };
        }
    },

    removeTrack: async (playlistId, trackId) => {
        try {
            const response = await api.delete(
                `audioposobie/playlists/${playlistId}/tracks/${trackId}/`
            );
            return { success: true, data: response.data };
        } catch (error) {
            const errorData = error.response?.data;
            return {
                success: false,
                error: errorData?.error || 'Ошибка при удалении трека',
            };
        }
    },
};

export default api;

